/**
 * Centralised date/time formatting. Backend speaks UTC ISO-8601; the SPA
 * renders in the browser's local timezone. All helpers accept either an
 * ISO string or a Date.
 *
 * <p>For display inside chat bubbles / banners. Calendar grid + meeting
 * row formatting still uses {@code Date.getHours()} / {@code getMinutes()}
 * via the helpers in {@code features/meetings/shared.ts} — those are
 * already local-time because the {@code Date} accessor methods return
 * local values.</p>
 */

const TIME_FMT = new Intl.DateTimeFormat(undefined, {
  hour: "2-digit",
  minute: "2-digit",
});

const DATE_TIME_FMT = new Intl.DateTimeFormat(undefined, {
  year: "numeric",
  month: "short",
  day: "numeric",
  hour: "2-digit",
  minute: "2-digit",
});

const DATE_FMT = new Intl.DateTimeFormat(undefined, {
  year: "numeric",
  month: "short",
  day: "numeric",
});

function toDate(input: string | Date): Date {
  return typeof input === "string" ? new Date(input) : input;
}

/** Local-tz date + time (e.g. "Jun 10, 2026, 11:00"). */
export function formatDateTime(input: string | Date): string {
  return DATE_TIME_FMT.format(toDate(input));
}

/** Local-tz time only (e.g. "11:00"). */
export function formatTimeOfDay(input: string | Date): string {
  return TIME_FMT.format(toDate(input));
}

/** Local-tz date only (e.g. "Jun 10, 2026"). */
export function formatDate(input: string | Date): string {
  return DATE_FMT.format(toDate(input));
}

/** Compact "start → end" range in local time, collapsing to one date when both fall on the same day. */
export function formatSlotRange(start: string | Date, end: string | Date): string {
  const s = toDate(start);
  const e = toDate(end);
  const sameDay =
    s.getFullYear() === e.getFullYear() &&
    s.getMonth() === e.getMonth() &&
    s.getDate() === e.getDate();
  return sameDay
    ? `${formatDateTime(s)} – ${formatTimeOfDay(e)}`
    : `${formatDateTime(s)} → ${formatDateTime(e)}`;
}
